import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../utils/platform';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { openExternalAuthFlow } from '../utils/external';
import { API_BASE } from '../../config/api';

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let payload = null;
  try { payload = await res.json(); } catch (_) {}
  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export function useGmbStatus() {
  // Vague 10 : refetch automatique au switch de restaurant.
  const { currentRestaurantId } = useCurrentRestaurant();
  const [connected, setConnected] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelRef = useRef(false);

  const getHeaders = useCallback(async () => {
    const token = await storage.get('token');
    const base = token
      ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
    return currentRestaurantId
      ? { ...base, 'X-Restaurant-Id': currentRestaurantId }
      : base;
  }, [currentRestaurantId]);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const data = await fetchJson(`${API_BASE}/api/gmb/status`, { headers });
      if (cancelRef.current) return;
      // ⚠️ Le backend retourne `connected` (pas `gmb_connected`) — cf audit Session 5.
      setConnected(!!data?.connected);
      setAccountId(data?.account_id || null);
      setLocationId(data?.location_id || null);
    } catch (e) {
      console.warn('[useGmbStatus] fetchStatus failed:', e.message);
      if (!cancelRef.current) {
        setConnected(false);
        setError(e.message);
      }
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [getHeaders]);

  const startOAuth = useCallback(async () => {
    const headers = await getHeaders();
    const data = await fetchJson(`${API_BASE}/api/gmb/auth/start`, { method: 'POST', headers });
    if (!data?.url) throw new Error('URL OAuth manquante');
    // Native : Safari View Controller in-app (préserve l'état React).
    // Web : top-level redirect (Google bloque iframes/popups).
    await openExternalAuthFlow(data.url);
    return data.url;
  }, [getHeaders]);

  const disconnect = useCallback(async () => {
    const headers = await getHeaders();
    await fetchJson(`${API_BASE}/api/gmb/disconnect`, { method: 'DELETE', headers });
    await fetchStatus();
  }, [getHeaders, fetchStatus]);

  useEffect(() => {
    cancelRef.current = false;
    fetchStatus();
    return () => { cancelRef.current = true; };
  }, [fetchStatus]);

  return { connected, accountId, locationId, loading, error, refresh: fetchStatus, startOAuth, disconnect };
}
